import React, { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { unctadApi } from '../../../services/api';
import type { FDIBySourceCountry } from '../../../services/api/unctadApi';

interface FDISourcePanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

// Country flag emojis
const COUNTRY_FLAGS: Record<string, string> = {
  CHN: '🇨🇳',
  RUS: '🇷🇺',
  SAU: '🇸🇦',
  TUR: '🇹🇷',
  ARE: '🇦🇪',
  KOR: '🇰🇷',
  DEU: '🇩🇪',
  USA: '🇺🇸',
  GBR: '🇬🇧',
  JPN: '🇯🇵',
};

export const FDISourcePanel: React.FC<FDISourcePanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [data, setData] = useState<FDIBySourceCountry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const response = await unctadApi.getFDIBySource('UZB');
      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  const exportData = data.map(d => ({
    sourceCountry: d.sourceCountryName,
    value: d.value,
    share: d.share,
    year: d.year,
    source: d.source,
  }));

  // Color scale for bars
  const getBarColor = (index: number) => {
    const colors = [
      'bg-data-amber',
      'bg-data-green',
      'bg-data-blue',
      'bg-emerald-500',
      'bg-cyan-500',
      'bg-violet-500',
      'bg-pink-500',
      'bg-orange-500',
      'bg-lime-500',
      'bg-indigo-500',
    ];
    return colors[index % colors.length];
  };

  return (
    <Panel
      id="fdi-source"
      title="FDI by Source"
      subtitle="Top Investors"
      status="static"
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col">
        {/* Source Badge */}
        <div className="flex items-center gap-2 mb-2 px-1">
          <DataSourceBadge source={DATA_SOURCES.UNCTAD} showLabel size="xs" />
          <span className="text-xs text-gray-500">2024</span>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-xs">
            Loading FDI sources...
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            {/* Top 3 Highlight */}
            <div className="grid grid-cols-3 gap-1 mb-3">
              {data.slice(0, 3).map((source, idx) => (
                <div
                  key={source.sourceCountry}
                  className={`p-1.5 rounded text-center ${
                    idx === 0 ? 'bg-data-amber/10 border border-data-amber/30' : 'bg-terminal-border/30'
                  }`}
                >
                  <div className="text-lg">{COUNTRY_FLAGS[source.sourceCountry] || '🏳️'}</div>
                  <div className="text-xs text-gray-300 truncate">{source.sourceCountryName}</div>
                  <div className={`text-sm font-mono font-bold ${
                    idx === 0 ? 'text-data-amber' : 'text-white'
                  }`}>
                    {source.share.toFixed(1)}%
                  </div>
                </div>
              ))}
            </div>

            {/* Full List */}
            <div className="space-y-1.5">
              {data.map((source, idx) => (
                <div key={source.sourceCountry} className="flex items-center gap-2">
                  <span className="text-sm">{COUNTRY_FLAGS[source.sourceCountry] || '🏳️'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-gray-300 truncate">
                        {source.sourceCountryName}
                      </span>
                      <span className="text-xs font-mono text-white">
                        ${(source.value / 1000).toFixed(1)}B
                      </span>
                    </div>
                    <div className="h-1.5 bg-terminal-border rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${getBarColor(idx)}`}
                        style={{ width: `${source.share * 3}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 w-10 text-right">
                    {source.share.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Insights */}
        <div className="mt-2 p-2 bg-terminal-border/30 rounded">
          <div className="flex items-center gap-1 mb-1">
            <Globe size={12} className="text-data-blue" />
            <span className="text-xs text-gray-400">Investment Diversification</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center">
            <div>
              <div className="text-sm font-mono text-data-amber">{data.length}+</div>
              <div className="text-xs text-gray-500">Source Countries</div>
            </div>
            <div>
              <div className="text-sm font-mono text-data-green">
                {data.slice(0, 5).reduce((sum, d) => sum + d.share, 0).toFixed(0)}%
              </div>
              <div className="text-xs text-gray-500">Top 5 Share</div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-2 pt-1 border-t border-terminal-border/50 text-xs text-gray-600">
          Source: UNCTAD / Central Bank of Uzbekistan
        </div>
      </div>
    </Panel>
  );
};

export default FDISourcePanel;
